import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import {
    CorrespondenceClientModel,
    CorrespondenceResponse, EmailRecipientTypeEnum, SubtaskCorrespondenceEmailModel,
    SubtaskStatusEnum
} from "../../../models/vetting/vetting.models";
import { RightShipDialogServiceConstants } from "../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import { SubtasksService } from "../../../services/subtasks/subtasks.service";
import { SignalFunctions } from "../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { ClientSideValidator, EntityValidationsService } from "../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../client-validation-system/validations.models";
import { Guid } from "../../../utilities/guid";
import { RightshipQiMessages } from "../../../configuration/rightshipqi-event-messages.constants";
import { RightshipEntityAttachmentModel } from "../../../models/attachment/attachment.models";
import { RightshipConfirmationDialogModel } from "../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { I18N } from "aurelia-i18n";
import { AttachmentClientService } from "../../../services/attachment/attachments-client.service";
import { computedFrom } from "aurelia-binding";
import { Reflection } from "../../../utilities/reflection";
import { RightshipEntityAttachmentManager } from "../../pages/modules/common-steps/rightship-entity-attachment/rightship-entity-attachment-manager";

//Load styles
require("../../../styles/correspondence/core-correspondence.scss");

@autoinject()
@transient()
export class LayoutCorrespondence {

    public showDatePicker: boolean = false;
    public messageSubmitted: boolean = false;
    public correspondenceMessage: string;
    public paste: EventListenerOrEventListenerObject;
    subtaskCorrespondenceId: number;
    validatorEmails: ClientSideValidator;
    validatorMessages: ClientSideValidator;

    constructor(private lookupClientService: LookupClientService, private dialogService: DialogClientService, private subtasksService: SubtasksService,
        private signalClientService: SignalClientService, private eventAggregator: EventAggregator,
        private notificationClientService: NotificationClientService, private attachmentDialogBroker: RightshipEntityAttachmentManager,
        private entityValidationsService: EntityValidationsService, private i18N: I18N,
        private attachmentClientService: AttachmentClientService) {
        this.paste = (e: any) => {
            if (!e.propagationStopped) {
                const items = ((e.clipboardData || (e.originalEvent || {}).clipboardData) || {}).items || [];
                for (const item of items) {
                    if (item.kind === 'file') {
                        const blob = item.getAsFile();
                        const reader = new FileReader();
                        reader.onload = (f) => {
                            eventAggregator.publish(RightshipQiMessages.paste, { source: (<any>f.target).result, fileName: blob.name });
                        };
                        reader.readAsDataURL(blob);
                    }
                }
            }
        };

    }

    correspondence: CorrespondenceResponse;
    toEmails: SubtaskCorrespondenceEmailModel[] = [];
    onSubtaskCorrespondenceReplyFunc: (x: CorrespondenceClientModel) => void;


    decodeUrl(encodedString) {
        // Use decodeURIComponent to replace all encoded characters
        return decodeURIComponent(encodedString.replace(/%([0-9A-Fa-f]{2})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16))));
    }

    async activate() {
        let guid = "";
        const params = window.location.pathname.split("/").filter(x => x != null && x != "");
        if (params.length > 1) {
            guid = params[1];
        }
        this.correspondence = await this.subtasksService.getCorrespondence(guid);


        //Boot up Intercom
        // (<any>window).Intercom("boot", {
        //     app_id:   window["intercomAppId"],
        //     email: this.correspondenceEmail,
        //     user_hash: window["intercom-hash"],
        //     custom_launcher_selector: '.intercom-trigger',
        //     hide_default_launcher: true
        // });

        if (this.correspondence && this.correspondence.correspondence.length > 0 && this.correspondence.correspondence[0].emails.length > 0 && !this.correspondence.isInvitation) {
            this.toEmails = this.correspondence.correspondence[0].emails;
            if (this.toEmails.length < 1) {
                this.toEmails.push({
                    canEdit: true,
                    email: "",
                    emailRecipientTypeId: EmailRecipientTypeEnum.To,
                    isShipManagerEmail: false,
                    isFromConfig: false,
                    isInspectingCompanyEmail: false,
                    isTechnicalManagerEmail: false
                }
                )
            }
        }
        else {
            this.toEmails = [{
                canEdit: true,
                email: "",
                emailRecipientTypeId: EmailRecipientTypeEnum.To,
                isShipManagerEmail: false,
                isFromConfig: false,
                isInspectingCompanyEmail: false,
                isTechnicalManagerEmail: false
            }];
        }

        this.eventAggregator.subscribe('show-date-picker', () => {
            this.showDatePicker = true;
        });

        this.eventAggregator.subscribe('hide-date-picker', () => {
            this.showDatePicker = false;
        });

        await this.lookupClientService.clearAllLookups();
    }

    onSubtaskCorrespondenceReply(x: any) {
        this.correspondence.correspondence.splice(0, 0, x);
    }

    public openAttachmentsDialog() {
        if (!this.correspondence) return;

        this.attachmentDialogBroker.rootItem = {
            entityType: EntityTypes.SubtaskCorrespondence,
            identifier: Guid.generateGuid()
        };
        this.attachmentDialogBroker.rootItem["id"] = this.subtaskCorrespondenceId;
        this.attachmentDialogBroker.saveFunc = async () => {
            const subtaskCorrespondenceReponse = await this.subtasksService.reply(this.correspondence.guid, this.subtaskCorrespondenceId, this.correspondenceMessage, false,
                false, false, [], this.toEmails, null, null, this.getEa());
            if (subtaskCorrespondenceReponse && subtaskCorrespondenceReponse.validationResult && subtaskCorrespondenceReponse.validationResult.errors) {
                this.notificationClientService.show(subtaskCorrespondenceReponse.validationResult.errors[0].message, "", NotificationType.Error)
                return;
            }
            this.subtaskCorrespondenceId = subtaskCorrespondenceReponse.subtaskCorrespondenceId;
            this.attachmentDialogBroker.rootItem["id"] = this.subtaskCorrespondenceId;
            return () => {
            };
        };
        this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipAttachmentsDialog, this.attachmentDialogBroker, true, false, false, 'right');
    }

    getWho(c: CorrespondenceClientModel) {
        if (c.user)
            return c.user;

        if (c.emails.length > 0) {
            const from = c.emails.filter(x => x.isFrom)[0];
            if (from) return from.email;
            return c.emails[0].email;
        }
        return null;
    }

    getWhome(c: CorrespondenceClientModel) {
        let str = "";
        if (c.emails.length > 0) {
            const to = c.emails.filter(x => !x.isFrom);
            for (const t of to) {
                if (!str)
                    str += `To: ${t.email}`;
                else
                    str += `<br/>To: ${t.email}`;
            }
        }
        return str;
    }

    getEa() {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const ea = urlParams.get('ea');
        return ea ? ea.replace(/ /g, '+') : ea;
    }

    public async submit() {

        try {
            if (this.correspondenceMessage && !$(this.correspondenceMessage).get(0).innerText.trim()) {
                this.correspondenceMessage = "";
            }
            const result = await this.validatorEmails.validate();
            if (!result.valid)
                return;

            const result2 = await this.validatorMessages.validate();
            if (!result2.valid)
                return;
            const subtaskCorrespondenceReponse = await this.subtasksService.reply(this.correspondence.guid, this.subtaskCorrespondenceId, this.correspondenceMessage, true, false,
                false, [], this.toEmails.filter(x => x.email), null, null, this.getEa());

            if (subtaskCorrespondenceReponse && subtaskCorrespondenceReponse.validationResult && subtaskCorrespondenceReponse.validationResult.errors) {
                this.notificationClientService.show(subtaskCorrespondenceReponse.validationResult.errors[0].message, "", NotificationType.Error)
                return
            }
            this.correspondenceMessage = "";
            this.subtaskCorrespondenceId = null;
            this.attachmentDialogBroker.items = [];
            if (this.attachmentDialogBroker.rootItem)
                this.attachmentDialogBroker.rootItem["id"] = null;

            //Hide conversation and show submitted UI
            this.messageSubmitted = true;

            // this.notificationClientService.show("Reply", "Thank you for your message. You will receive an email when RightShip respond back to you.", NotificationType.Success, 5000);
        } catch {
            this.notificationClientService.show("Reply", "Failed to send reply.", NotificationType.Error);
        }
    }

    public async submitInvitation() {

        try {
            const result = await this.validatorEmails.validate();
            if (!result.valid)
                return;

            const response = await this.subtasksService.acceptInvitation(this.correspondence.guid, this.subtaskCorrespondenceId, this.toEmails.filter(x => x.email));
            this.notificationClientService.show("Accept invitation", "Thank you for accepting invitation. You will receive an email with a new link where you can respond back to RightShip.", NotificationType.Success);
            window.location.href = `/correspondence/${response.id}?ea=${response.encodedEmail}`;
        } catch {
            this.notificationClientService.show("Accept invitation", "Failed to accept invitation.", NotificationType.Error);
        }
    }

    @computedFrom(Reflection.getPropertyPath<LayoutCorrespondence>(z => z.correspondence),
        Reflection.getPropertyPath<LayoutCorrespondence>(z => z.correspondence.subtaskStatusId))
    get conversationClosed() {
        return !this.correspondence || this.correspondence.subtaskStatusId == SubtaskStatusEnum.Closed;
    }

    get lastRightShipUser() {
        if (!this.correspondence) return null;

        const users = this.correspondence.correspondence.filter(x => x.isFromRightShip).map(x => x.user);
        return users[users.length - 1];
    }

    @computedFrom(Reflection.getPropertyPath<LayoutCorrespondence>(z => z.toEmails), Reflection.getPropertyPath<LayoutCorrespondence>(z => z.toEmails.length))
    get hasEmailsFromConfig() {
        return this.toEmails.filter(x => x.isFromConfig).length > 0;
    }

    attached() {
        this.onSubtaskCorrespondenceReplyFunc = this.onSubtaskCorrespondenceReply.bind(this);
        this.signalClientService.subscribeToGroup(SignalFunctions.onSubtaskCorrespondenceReply, `correspondence-${this.correspondence.guid}`, this.onSubtaskCorrespondenceReply);
        document.addEventListener("click", this.documentClick);
        document.addEventListener("paste", this.paste);
        const rulesHolderEmails = {
            activation: ValidationActivation.Always,
            entityName: "Correspondence Emails",
            isCollection: true,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Email is required",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Email is not in correct format",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Email
            }]
        };
        this.validatorEmails = this.entityValidationsService.getValidator(this.toEmails, rulesHolderEmails);

        const rulesHolderMessage = {
            activation: ValidationActivation.Always,
            entityName: "Correspondence Message",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Correspondence Message is required",
                parameters: null, property: "correspondenceMessage",
                validationType: ValidationTypeEnum.Required
            }]
        };

        this.validatorMessages = this.entityValidationsService.getValidator(this, rulesHolderMessage);
    }

    addEmail() {
        this.toEmails.push({
            email: "",
            emailRecipientTypeId: EmailRecipientTypeEnum.To,
            isTechnicalManagerEmail: false,
            isShipManagerEmail: false,
            isFromConfig: false,
            isInspectingCompanyEmail: false,
            canEdit: true
        });
    }

    removeEmail(index: number) {
        this.toEmails.splice(index, 1);
    }

    detached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.onSubtaskCorrespondenceReply, `correspondence-${this.correspondence.guid}`, this.onSubtaskCorrespondenceReply);
        document.removeEventListener("click", this.documentClick);
        document.removeEventListener("paste", this.paste);
    }

    public documentClick: EventListenerOrEventListenerObject = e => {
        if (!(<any>e).propagationStopped) {
            this.eventAggregator.publish(Messages.documentClick, { source: "document" });
        }
    };

    get isVet() {
        return this.correspondence.entityTypeId == EntityTypes.Vet;
    }

    goBack() {
        this.messageSubmitted = false;
    }


    public removeAttachment($event, item: RightshipEntityAttachmentModel) {
        $event.stopPropagation();

        const model: RightshipConfirmationDialogModel = {
            title: "Delete Document",
            messages: ["Are you sure you want to delete this document?"],
            cancel: "Cancel",
            confirm: "Delete",
            messageClasses: [],
            onConfirm: () => {
                this.removeAttachmentInternal($event, item).then(() => {
                    this.notificationClientService.show(this.i18N.tr("attachment-was-deleted-text"), "", NotificationType.Success);
                    this.dialogService.hide();
                }).catch(() => {
                    this.notificationClientService.show(this.i18N.tr("error-text") + "!", this.i18N.tr("attachment-could-not-be-deleted-text"), NotificationType.Error);
                    this.dialogService.hide();
                });
            }
        };

        this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);
    }

    protected removeAttachmentInternal($event, item: RightshipEntityAttachmentModel) {
        $event.stopPropagation();
        return this.attachmentClientService.deleteCorrespondenceAttachments({
            entityAttachmentIds: [item.entityAttachmentId],
            correspondenceGuid: this.correspondence.guid
        }).then(() => {

            if (!this.attachmentDialogBroker.items) {
                this.attachmentDialogBroker.items = [];
            }
            const index = this.attachmentDialogBroker.items.indexOf(item);
            if (index > -1) {
                this.attachmentDialogBroker.items.splice(index, 1);
            }

            if (this.attachmentDialogBroker.onAttachmentsModified) {
                this.attachmentDialogBroker.onAttachmentsModified([]);
            }
        });
    }
}
